/**
 * librarySelectors.js
 * Selectores puros y validadores de dominio para bibliotecas por proyecto,
 * biblioteca global y aislamiento de recursos (Fase 2B.4B Â· Paso 2).
 */

export const normalizeHandle = (h = "") =>
  String(h)
    .trim()
    .toLowerCase()
    .replace(/^@+/, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]/g, "");

/**
 * Devuelve Ãºnicamente los personajes propios del proyecto o expresamente
 * vinculados desde la biblioteca global.
 */
export const getProjectCharacters = (state = {}, projectId = null) => {
  if (!projectId) return [];
  const project = (state.projects || []).find((p) => p.id === projectId);
  if (!project) return [];

  const linkedIds = new Set(project.linkedResourceIds || []);
  const allCharacters = state.characters || [];

  return allCharacters.filter((c) => {
    // 1. Recursos explÃ­citamente propios del proyecto
    if (c.projectId === projectId) {
      return true;
    }
    // 2. Recursos globales expresamente vinculados al proyecto
    if (c.scope === "global" && linkedIds.has(c.id)) {
      return true;
    }
    // 3. Compatibilidad con fixture Demo histÃ³rico de prj_demo01 si aÃºn no tiene projectId asignado
    if (projectId === "prj_demo01" && c.isDemo && !c.projectId) {
      return true;
    }
    return false;
  });
};

/**
 * Devuelve Ãºnicamente las referencias propias del proyecto o expresamente
 * vinculadas desde la biblioteca global (o pertenecientes a personajes visibles del proyecto).
 */
export const getProjectReferences = (state = {}, projectId = null) => {
  if (!projectId) return [];
  const project = (state.projects || []).find((p) => p.id === projectId);
  if (!project) return [];

  const linkedIds = new Set(project.linkedResourceIds || []);
  const visibleCharacterIds = new Set(getProjectCharacters(state, projectId).map((c) => c.id));
  const allReferences = state.references || [];

  return allReferences.filter((r) => {
    // 1. Referencias propias del proyecto
    if (r.projectId === projectId) {
      return true;
    }
    // 2. Referencias vinculadas directamente o cuyo personaje estÃ¡ visible en el proyecto
    if (r.scope === "global" && (linkedIds.has(r.id) || (r.characterId && linkedIds.has(r.characterId)))) {
      return true;
    }
    if (r.characterId && visibleCharacterIds.has(r.characterId)) {
      return true;
    }
    // 3. Compatibilidad con fixture Demo histÃ³rico de prj_demo01
    if (projectId === "prj_demo01" && r.isDemo && !r.projectId) {
      return true;
    }
    return false;
  });
};

/**
 * Devuelve los personajes pertenecientes a la biblioteca global.
 */
export const getGlobalCharacters = (state = {}) => {
  return (state.characters || []).filter((c) => c.scope === "global");
};

/**
 * Devuelve las referencias pertenecientes a la biblioteca global.
 */
export const getGlobalReferences = (state = {}) => {
  return (state.references || []).filter((r) => r.scope === "global");
};

/**
 * Devuelve recursos sin propietario confirmado (pendientes de revisiÃ³n/triage).
 * Estos recursos no se inyectan en ningÃºn proyecto ni en la biblioteca global.
 */
export const getPendingReviewResources = (state = {}) => {
  const characters = (state.characters || []).filter(
    (c) => c.scope === "pending_review" || c.reviewStatus === "pending_review" || (!c.projectId && c.scope !== "global" && !c.isDemo)
  );
  const references = (state.references || []).filter(
    (r) => r.scope === "pending_review" || r.reviewStatus === "pending_review" || (!r.projectId && r.scope !== "global" && !r.isDemo)
  );
  return { characters, references };
};

/**
 * Valida colisiones de handle (@slug) dentro de cada Ã¡mbito especÃ­fico.
 * - En un proyecto: valida contra los personajes propios y globales vinculados al proyecto.
 * - En el Ã¡mbito global: valida contra todos los personajes globales.
 */
export const validateHandleCollision = (state = {}, { handle, projectId = null, scope = null, excludeId = null }) => {
  const norm = normalizeHandle(handle);
  if (!norm) {
    return { valid: false, reason: "El handle no puede estar vacÃ­o." };
  }

  const effectiveScope = scope || (projectId ? "project" : "pending_review");

  if (effectiveScope === "project" && projectId) {
    const visibleChars = getProjectCharacters(state, projectId).filter((c) => c.id !== excludeId);
    const hit = visibleChars.find((c) => normalizeHandle(c.handle) === norm);
    if (hit) {
      return {
        valid: false,
        reason: `ColisiÃ³n de handle: @${norm} ya estÃ¡ en uso en el proyecto ${projectId}.`,
        conflictingId: hit.id,
      };
    }
  } else if (effectiveScope === "global") {
    const globalChars = getGlobalCharacters(state).filter((c) => c.id !== excludeId);
    const hit = globalChars.find((c) => normalizeHandle(c.handle) === norm);
    if (hit) {
      return {
        valid: false,
        reason: `ColisiÃ³n de handle: @${norm} ya estÃ¡ en uso en la biblioteca global.`,
        conflictingId: hit.id,
      };
    }
  }

  return { valid: true };
};

/**
 * Valida que una referencia o personaje pertenezca al Ã¡mbito autorizado del proyecto.
 * Rechaza referencias cuyo proyecto propietario no coincida con el Ã¡mbito autorizado.
 */
export const validateReferenceAuthorization = (state = {}, { referenceId = null, characterId = null, projectId }) => {
  if (!projectId) {
    return { authorized: false, reason: "Se requiere un projectId para validar autorizaciÃ³n." };
  }

  const project = (state.projects || []).find((p) => p.id === projectId);
  if (!project) {
    return { authorized: false, reason: `Proyecto ${projectId} no existe.` };
  }

  // Si se provee characterId, validar el personaje
  if (characterId) {
    const char = (state.characters || []).find((c) => c.id === characterId);
    if (!char) {
      return { authorized: false, reason: `Personaje ${characterId} no encontrado.` };
    }
    if (char.scope === "pending_review" || char.reviewStatus === "pending_review") {
      return { authorized: false, reason: "El recurso estÃ¡ pendiente de revisiÃ³n y no tiene propietario confirmado." };
    }
    if (char.scope === "project" && char.projectId !== projectId) {
      return {
        authorized: false,
        reason: `Ãmbito no autorizado: el recurso pertenece al proyecto ${char.projectId} y no puede usarse en ${projectId}.`,
      };
    }
    if (char.scope === "global" && !(project.linkedResourceIds || []).includes(char.id)) {
      return {
        authorized: false,
        reason: `El recurso global ${characterId} no estÃ¡ expresamente vinculado al proyecto ${projectId}.`,
      };
    }
  }

  // Si se provee referenceId, validar la referencia
  if (referenceId) {
    const ref = (state.references || []).find((r) => r.id === referenceId);
    if (!ref) {
      return { authorized: false, reason: `Referencia ${referenceId} no encontrada.` };
    }
    if (ref.scope === "pending_review" || ref.reviewStatus === "pending_review") {
      return { authorized: false, reason: "La referencia estÃ¡ pendiente de revisiÃ³n y no tiene propietario confirmado." };
    }
    if (ref.projectId && ref.projectId !== projectId) {
      return {
        authorized: false,
        reason: `Ãmbito no autorizado: la referencia pertenece al proyecto ${ref.projectId} y no puede usarse en ${projectId}.`,
      };
    }
    if (ref.scope === "global") {
      const linked = (project.linkedResourceIds || []).includes(ref.id) ||
        (ref.characterId && (project.linkedResourceIds || []).includes(ref.characterId));
      if (!linked) {
        return {
          authorized: false,
          reason: `La referencia global ${referenceId} no estÃ¡ vinculada al proyecto ${projectId}.`,
        };
      }
    }
    if (ref.characterId) {
      const charCheck = validateReferenceAuthorization(state, { characterId: ref.characterId, projectId });
      if (!charCheck.authorized) return charCheck;
    }
  }

  return { authorized: true };
};

/**
 * EvalÃºa la polÃ­tica de ediciÃ³n de un recurso global para evitar que
 * alteraciones silenciosas afecten producciones en curso.
 */
export const evaluateGlobalEditPolicy = (state = {}, characterId, { requestedProjectId = null, allowGlobalMutation = false } = {}) => {
  const char = (state.characters || []).find((c) => c.id === characterId);
  if (!char || char.scope !== "global") {
    return { isGlobal: false, allowed: true, requiresFork: false, affectedProjects: [] };
  }

  const affectedProjects = (state.projects || []).filter((p) =>
    (p.linkedResourceIds || []).includes(characterId)
  );

  // Si la ediciÃ³n se solicita desde dentro de un proyecto especÃ­fico,
  // la polÃ­tica segura exige crear una bifurcaciÃ³n local (fork / copy-on-write).
  if (requestedProjectId) {
    return {
      isGlobal: true,
      allowed: true,
      requiresFork: true,
      affectedProjects: affectedProjects.map((p) => p.id),
      reason: "EdiciÃ³n iniciada desde un proyecto: se aplica bifurcaciÃ³n local (copy-on-write) para proteger la biblioteca global y otros proyectos.",
    };
  }

  // Si la ediciÃ³n es global y hay proyectos que la utilizan:
  // Se requiere confirmaciÃ³n explÃ­cita (allowGlobalMutation: true) para evitar cambios silenciosos.
  if (affectedProjects.length > 0 && !allowGlobalMutation) {
    return {
      isGlobal: true,
      allowed: false,
      requiresFork: false,
      affectedProjects: affectedProjects.map((p) => p.id),
      reason: `El recurso global estÃ¡ vinculado en ${affectedProjects.length} proyectos. Se requiere confirmaciÃ³n explÃ­cita de mutaciÃ³n global o bifurcaciÃ³n local para no alterar producciones en curso.`,
    };
  }

  return {
    isGlobal: true,
    allowed: true,
    requiresFork: false,
    affectedProjects: affectedProjects.map((p) => p.id),
  };
};

/**
 * Devuelve los activos autorizados para un proyecto especÃ­fico:
 * - Activos con projectId === projectId
 * - Activos vinculados a travÃ©s de personajes autorizados del proyecto (masterAssetId, viewAssetIds, views)
 * - Activos vinculados a travÃ©s de referencias autorizadas del proyecto
 * - Activos sin proyecto exclusivo (unassigned / globales) si no pertenecen a otro proyecto
 * Excluye activos pertenecientes a OTROS proyectos.
 */
export const getAuthorizedProjectAssets = (state = {}, projectId = null) => {
  if (!projectId) return [];
  const project = (state.projects || []).find((p) => p.id === projectId);
  if (!project) return [];

  const projectChars = getProjectCharacters(state, projectId);
  const projectRefs = getProjectReferences(state, projectId);

  const authorizedAssetIds = new Set();

  for (const c of projectChars) {
    if (c.masterAssetId) authorizedAssetIds.add(c.masterAssetId);
    (c.viewAssetIds || []).forEach((id) => authorizedAssetIds.add(id));
    (c.views || []).forEach((v) => {
      if (v?.assetId) authorizedAssetIds.add(v.assetId);
    });
  }

  for (const r of projectRefs) {
    if (r.assetId) authorizedAssetIds.add(r.assetId);
  }

  for (const s of (state.scenes || [])) {
    if (s.projectId === projectId) {
      if (s.selectedAssetId) authorizedAssetIds.add(s.selectedAssetId);
      (s.assetIds || []).forEach((id) => {
        if (id) authorizedAssetIds.add(id);
      });
    }
  }

  return (state.assets || []).filter((a) => {
    if (!a || !a.id) return false;
    // Pertenencia directa al proyecto
    if (a.projectId === projectId) return true;
    // Vinculado a travÃ©s de personaje o referencia del proyecto
    if (authorizedAssetIds.has(a.id)) return true;
    // Si pertenece a otro proyecto explÃ­cito distinto, no estÃ¡ autorizado
    if (a.projectId && a.projectId !== projectId) return false;
    // Activo sin proyecto exclusivo asignado (p. ej. generado en QuickTool o biblioteca general)
    if (!a.projectId && !a.isDemo) return true;
    if (projectId === "prj_demo01" && a.isDemo && !a.projectId) return true;
    return false;
  });
};


/**
 * Devuelve todos los activos de la galería del usuario compatibles con medios (imagen / vídeo),
 * sin excluir activos pertenecientes a otros proyectos ni a fichas de referencia.
 */
export const getAllUserGalleryAssets = (state = {}, filter = (a) => a.type === "image" || a.type === "video") => {
  return (state.assets || []).filter((a) => {
    if (!a || !a.id) return false;
    return typeof filter === "function" ? filter(a) : true;
  });
};